/**
 * Font Face Loader — loads uploaded font binaries via the FontFace API.
 * Google Fonts are skipped (loaded via CDN link in index.html).
 * Adobe Fonts are skipped (loaded via Typekit CSS).
 * Module-level cache prevents duplicate fetches across components.
 */
import { getToken } from '../api/client.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Tracks which font IDs have been fetched (or are in-flight)
const loaded = new Set();

async function loadFont(font) {
  const key = `${font.id}`;
  if (loaded.has(key)) return;
  loaded.add(key);

  if (!font.preview_file_id) return;

  try {
    const resp = await fetch(
      `${API_BASE}/fonts/${font.id}/serve/${font.preview_file_id}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    if (!resp.ok) {
      loaded.delete(key);
      return;
    }

    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Use FontFace API — each font is independently registered,
    // no shared state so no race condition between concurrent loads
    const face = new FontFace(font.family, `url(${blobUrl})`);
    await face.load();
    document.fonts.add(face);
  } catch {
    // Allow retry on failure
    loaded.delete(key);
  }
}

export function ensureLoaded(font) {
  if (!font) return;
  if (font.source === 'google-fonts' || font.source === 'adobe-fonts') return;
  if (loaded.has(`${font.id}`)) return;
  loadFont(font);
}
